// Session-scoped PIN gate for stock-modifying actions.
// PIN is set/managed in the Pengaturan page. When set, the modal is opened
// (via `requireStockUnlock`) exactly once per browser session — after which
// all subsequent mutations in the same session pass through immediately.

import api from "./api";

const SESSION_KEY = "stock_pin_unlocked";

let pinStatusCache = null;
let pinStatusPromise = null;
let modalHandler = null; // set by StockLockModal on mount

export function registerLockModal(handler) {
  modalHandler = handler;
}

export function markUnlocked() {
  sessionStorage.setItem(SESSION_KEY, "1");
}

export function markLocked() {
  sessionStorage.removeItem(SESSION_KEY);
}

export function isUnlocked() {
  return sessionStorage.getItem(SESSION_KEY) === "1";
}

export function refreshPinStatus() {
  pinStatusCache = null;
  pinStatusPromise = null;
}

async function fetchPinStatus() {
  if (pinStatusCache) return pinStatusCache;
  if (!pinStatusPromise) {
    pinStatusPromise = api
      .get("/settings/pin-status")
      .then((r) => {
        pinStatusCache = r.data || { pin_set: false };
        pinStatusPromise = null;
        return pinStatusCache;
      })
      .catch(() => {
        pinStatusPromise = null;
        return { pin_set: false };
      });
  }
  return pinStatusPromise;
}

/**
 * Ensure the user has unlocked stock modifications this session.
 *
 * Resolves to `true` if the mutation may proceed (no PIN set, already
 * unlocked, or user entered the correct PIN just now). Resolves to
 * `false` when the user cancels the PIN prompt.
 *
 * When `force` is `true`, the session cache is bypassed and the PIN
 * modal is always shown (useful for high-impact actions such as saving
 * a new/updated product record).
 */
export async function requireStockUnlock(options = {}) {
  const status = await fetchPinStatus();
  if (!status.pin_set) return true;
  if (!options.force && isUnlocked()) return true;
  if (!modalHandler) return true; // fail-open if modal isn't mounted yet
  return await modalHandler();
}
